// ==========================================
// src/pages/CargaDetalhes.jsx
// ✅ SEM WHATSAPP — Motorista não sai mais da tela
// ==========================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  MapPin, ArrowRight, Calendar, Weight, Truck,
  DollarSign, ArrowLeft, Loader2, CheckCircle,
  FileText, Lock, Building2, Package, CheckCircle2,
  AlertTriangle, CreditCard, Star, Eye, Phone,
  MessageCircle, User, FileCheck, Shield, Zap,
  Bell, Send
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { supabase } from '@/lib/supabase';

const tipoVeiculoLabels = {
  truck: 'Truck', carreta: 'Carreta', bitrem: 'Bitrem', rodotrem: 'Rodotrem',
  van: 'Van', toco: 'Toco', tres_quartos: '3/4', vuc: 'VUC', prancha: 'Prancha',
  furgão: 'Furgão'
};

const tipoCarroceriaLabels = {
  aberta: 'Aberta', fechada: 'Fechada', bau: 'Baú', sider: 'Sider',
  graneleira: 'Graneleira', tanque: 'Tanque', cegonha: 'Cegonha',
  prancha: 'Prancha', boiadeiro: 'Boiadeiro', cacamba: 'Caçamba'
};

const tipoCargaLabels = {
  geral: 'Geral', granel_solido: 'Granel Sólido', granel_liquido: 'Granel Líquido',
  refrigerada: 'Refrigerada', perigosa: 'Perigosa', viva: 'Viva',
  indivisivel: 'Indivisível', container: 'Container'
};

const statusColors = {
  disponivel: 'bg-green-100 text-green-800',
  negociando: 'bg-yellow-100 text-yellow-800',
  aceita: 'bg-blue-100 text-blue-800',
  em_transito: 'bg-purple-100 text-purple-800',
  entregue: 'bg-slate-100 text-slate-800',
  cancelada: 'bg-red-100 text-red-800',
  aprovada: 'bg-emerald-100 text-emerald-800',
  paga: 'bg-indigo-100 text-indigo-800',
  concluida: 'bg-slate-100 text-slate-800',
  aguardando: 'bg-amber-100 text-amber-800'
};

const statusLabels = {
  disponivel: 'Disponível', negociando: 'Negociando', aceita: 'Aceita',
  em_transito: 'Em Trânsito', entregue: 'Entregue', cancelada: 'Cancelada',
  aprovada: 'Aprovada', paga: 'Pagamento Confirmado', concluida: 'Concluída',
  aguardando: 'Aguardando'
};

const formatarValor = (valor) => {
  const num = Number(valor);
  if (isNaN(num) || num === 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(num);
};

const formatarPeso = (peso, unidade) => {
  if (!peso) return 'Não informado';
  const num = parseFloat(peso.toString().replace(/[^\d.]/g, ''));
  if (isNaN(num)) return peso;
  const numFormatado = num.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: unidade === 'm3' ? 1 : 0
  });
  const unidadeLabel = unidade === 'ton' ? 'ton' : 
                      unidade === 'kg' ? 'kg' : 
                      unidade === 'm3' ? 'm³' : 
                      unidade || '';
  return `${numFormatado} ${unidadeLabel}`;
};

const isPropriaCargaEmpresa = (carga, user) => {
  if (!user || user.tipo !== 'empresa') return false;
  return carga?.created_by === user.email || carga?.empresa_id === user.id;
};

const logTentativaConsultaPropriaCarga = (carga, user, origem) => {
  const logs = JSON.parse(localStorage.getItem('borafrete_logs_consulta_propria') || '[]');
  const novoLog = {
    timestamp: new Date().toISOString(),
    cargaId: carga.id,
    cargaTitulo: carga.titulo,
    userEmail: user?.email,
    userTipo: user?.tipo,
    userId: user?.id,
    action: 'tentativa_acesso_detalhes',
    origem: origem || 'CargaDetalhes'
  };
  logs.push(novoLog);
  localStorage.setItem('borafrete_logs_consulta_propria', JSON.stringify(logs.slice(-100)));
  console.log('📊 [MONITORAMENTO] Empresa acessou detalhes da própria carga:', novoLog);
};

function BotaoOnlinePiscante({ ativo }) {
  if (!ativo) return null;
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-200 animate-pulse shadow-sm">
      <Zap className="w-3.5 h-3.5 fill-emerald-500 text-emerald-500" />
      Disponível
    </span>
  );
}

function InfoBox({ icon: Icon, label, value, highlight = false }) {
  return (
    <div className={`p-4 rounded-xl border ${highlight ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-100'}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className={`w-4 h-4 ${highlight ? 'text-blue-600' : 'text-gray-400'}`} />
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
      </div>
      <p className={`font-bold text-sm ${highlight ? 'text-blue-900' : 'text-gray-900'}`}>{value || '—'}</p>
    </div>
  );
}

export default function CargaDetalhes() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: cargaId } = useParams();

  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const [carga, setCarga] = useState(null);
  const [isLoadingCarga, setIsLoadingCarga] = useState(true);
  const [propostas, setPropostas] = useState([]);
  const [contratosRecebidos, setContratosRecebidos] = useState([]);
  const [veiculosMotorista, setVeiculosMotorista] = useState([]);
  const [showPropriaCargaAlert, setShowPropriaCargaAlert] = useState(false);
  const [motoristaData, setMotoristaData] = useState(null);
  const [loadingMotorista, setLoadingMotorista] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login?from=' + encodeURIComponent(window.location.pathname));
    }
  }, [authLoading, isAuthenticated, navigate]);

  // ==========================================
  // ✅ Busca motorista com COLUNAS QUE EXISTEM
  // ==========================================
  useEffect(() => {
    const buscarMotorista = async () => {
      if (!user?.id || user?.tipo !== 'motorista') return;
      try {
        setLoadingMotorista(true);
        
        const { data, error } = await supabase
          .from('motoristas')
          .select('cpf, telefone, nome_completo, email, placa_caminhao, cnh, categoria_cnh')
          .eq('usuario_id', user.id)
          .single();
          
        if (error) {
          console.log('⚠️ Supabase erro (normal se não tem motorista cadastrado):', error.message);
          const motoristas = JSON.parse(localStorage.getItem('borafrete_motoristas') || '[]');
          const motoristaLocal = motoristas.find(m => m.usuario_id === user.id || m.email === user.email);
          if (motoristaLocal) {
            setMotoristaData({
              cpf: motoristaLocal.cpf,
              telefone: motoristaLocal.telefone,
              nome_completo: motoristaLocal.nome_completo || motoristaLocal.nome,
              email: motoristaLocal.email,
              placa_caminhao: motoristaLocal.placa_caminhao || motoristaLocal.placa_veiculo,
              cnh: motoristaLocal.cnh,
              categoria_cnh: motoristaLocal.categoria_cnh
            });
          }
        } else if (data) {
          setMotoristaData(data);
        }
      } catch (err) {
        console.error('[CargaDetalhes] Erro ao buscar motorista:', err);
      } finally {
        setLoadingMotorista(false);
      }
    };
    buscarMotorista();
  }, [user]);

  useEffect(() => {
    const fetchCarga = async () => {
      if (!cargaId) return;
      try {
        setIsLoadingCarga(true);
        const listaNegra = JSON.parse(localStorage.getItem('borafrete_cargas_excluidas') || '[]');
        if (listaNegra.includes(cargaId)) {
          setCarga(null);
          toast.error('Esta carga foi removida da plataforma.');
          navigate('/buscar-cargas');
          return;
        }

        const { data: cargaData, error: cargaError } = await supabase
          .from('cargas')
          .select('*')
          .eq('id', cargaId)
          .single();

        let cargaEncontrada = null;
        if (cargaError || !cargaData) {
          const todasCargas = JSON.parse(localStorage.getItem('borafrete_cargas') || '[]');
          cargaEncontrada = todasCargas.find(c => c.id === cargaId);
          if (!cargaEncontrada) {
            const cargasPendentes = JSON.parse(localStorage.getItem('borafrete_cargas_pendentes') || '[]');
            cargaEncontrada = cargasPendentes.find(c => c.id === cargaId);
          }
        } else {
          cargaEncontrada = cargaData;
        }

        if (cargaEncontrada && listaNegra.includes(cargaEncontrada.id)) {
          setCarga(null);
          toast.error('Esta carga foi removida da plataforma.');
          navigate('/buscar-cargas');
          return;
        }

        setCarga(cargaEncontrada);

        if (cargaEncontrada) {
          document.title = `Frete ${cargaEncontrada.origem_cidade} → ${cargaEncontrada.destino_cidade} | ${cargaEncontrada.tipo_carga || 'Carga'} ${cargaEncontrada.peso_valor || ''}${cargaEncontrada.peso_unidade || ''} | BoraFrete`;
          let metaDesc = document.querySelector('meta[name="description"]');
          if (!metaDesc) {
            metaDesc = document.createElement('meta');
            metaDesc.name = 'description';
            document.head.appendChild(metaDesc);
          }
          metaDesc.content = `Transporte de ${cargaEncontrada.tipo_carga || 'carga'} de ${cargaEncontrada.origem_cidade}/${cargaEncontrada.origem_estado} para ${cargaEncontrada.destino_cidade}/${cargaEncontrada.destino_estado}. Peso: ${cargaEncontrada.peso_valor || 'N/I'}${cargaEncontrada.peso_unidade || ''}. Valor: R$ ${cargaEncontrada.valor_frete || 'a combinar'}. Contrate motorista autônomo via BoraFrete.`;
        }

        if (cargaEncontrada && isPropriaCargaEmpresa(cargaEncontrada, user)) {
          logTentativaConsultaPropriaCarga(cargaEncontrada, user, 'CargaDetalhes');
          setShowPropriaCargaAlert(true);
          toast.info('💡 Você está visualizando sua própria carga. Gerencie-a no painel "Minhas Cargas".', {
            duration: 5000,
            action: { label: 'Ir para Painel', onClick: () => navigate('/minhas-cargas') }
          });
        }

        const todasPropostas = JSON.parse(localStorage.getItem('borafrete_propostas') || '[]');
        setPropostas(todasPropostas.filter(p => p.carga_id === cargaId));
        const todosContratos = JSON.parse(localStorage.getItem('borafrete_contratos') || '[]');
        setContratosRecebidos(todosContratos.filter(c => c.carga_id === cargaId));

        if (user?.email) {
          const todosVeiculos = JSON.parse(localStorage.getItem('borafrete_veiculos') || '[]');
          const veiculosUser = todosVeiculos.filter(v => v.created_by === user.email);
          setVeiculosMotorista(veiculosUser);
          if (veiculosUser.length > 0 && !user.placa_veiculo) {
            user.placa_veiculo = veiculosUser[0].placa;
          }
        }
      } catch (error) {
        console.error('Erro ao buscar carga:', error);
      } finally {
        setIsLoadingCarga(false);
      }
    };
    fetchCarga();
  }, [cargaId, user, navigate]);

  const contrato = contratosRecebidos.find(c => c.motorista_email === user?.email);
  const isOwner = user && carga?.created_by === user.email;
  const jaTemContrato = !!contrato;
  const isPropria = isPropriaCargaEmpresa(carga, user);

  // ==========================================
  // ✅ Botão "Contratar" — SEM WhatsApp
  // ==========================================
  const handleContratarCarga = async () => {
    if (!carga) {
      toast.error('Carga não encontrada');
      return;
    }

    if (!user || user.tipo !== 'motorista') {
      toast.error('Apenas motoristas podem contratar cargas.');
      return;
    }

    const motoristaIdReal = user?.id;

    console.log('🔍 MOTORISTA ID:', motoristaIdReal);

    if (!motoristaIdReal) {
      toast.error('Erro de autenticação. Faça login novamente.');
      return;
    }

    const nomeMotorista = motoristaData?.nome_completo || user.nome || user.email;
    const cpfMotorista = motoristaData?.cpf || '';
    const telefoneMotorista = motoristaData?.telefone || '';
    const emailMotorista = motoristaData?.email || user.email || '';
    const placaMotorista = motoristaData?.placa_caminhao || user.placa_veiculo || '';

    console.log('[CargaDetalhes] Dados do motorista:', {
      nome: nomeMotorista,
      cpf: cpfMotorista,
      telefone: telefoneMotorista,
      email: emailMotorista,
      placa: placaMotorista,
      motoristaId: motoristaIdReal
    });

    if (!cpfMotorista) {
      toast.error('CPF não cadastrado. Complete seu perfil primeiro.', {
        action: { label: 'Completar Perfil', onClick: () => navigate('/perfil') }
      });
      return;
    }

    const cpfLimpo = cpfMotorista.replace(/\D/g, '');
    if (cpfLimpo.length !== 11) {
      toast.error('CPF inválido. Verifique seu cadastro.');
      return;
    }

    if (!telefoneMotorista) {
      toast.error('Telefone não cadastrado. Complete seu perfil primeiro.', {
        action: { label: 'Completar Perfil', onClick: () => navigate('/perfil') }
      });
      return;
    }

    if (!emailMotorista) {
      toast.error('Email não cadastrado. Complete seu perfil primeiro.');
      return;
    }

    // Gera ID da consulta LOCALMENTE primeiro (garantia)
    const consultaId = crypto.randomUUID();

    // Monta objeto da consulta
    const consultaLocal = {
      id: consultaId,
      carga_id: carga.id,
      motorista_id: motoristaIdReal,
      motorista_nome: nomeMotorista,
      motorista_email: emailMotorista,
      motorista_cpf: cpfLimpo,
      motorista_telefone: telefoneMotorista.replace(/\D/g, ''),
      motorista_celular: telefoneMotorista.replace(/\D/g, ''),
      motorista_placa: placaMotorista,
      motorista_veiculo: tipoVeiculoLabels[carga.tipo_veiculo] || carga.tipo_caminhao || 'Não informado',
      status: 'aguardando',
      created_at: new Date().toISOString(),
      expiraEm: new Date(Date.now() + 600000).toISOString(),
      carga: carga
    };

    try {
      toast.info('Enviando solicitação...', { duration: 2000 });

      // Tenta salvar no Supabase (mas não bloqueia se falhar)
      const { data: rpcResult, error: rpcError } = await supabase.rpc('salvar_consulta', {
        p_carga_id: carga.id,
        p_motorista_id: motoristaIdReal,
        p_motorista_nome: nomeMotorista,
        p_motorista_telefone: telefoneMotorista.replace(/\D/g, ''),
        p_motorista_celular: telefoneMotorista.replace(/\D/g, ''),
        p_motorista_placa: placaMotorista,
        p_motorista_email: emailMotorista,
        p_motorista_cpf: cpfLimpo,
        p_motorista_veiculo: tipoVeiculoLabels[carga.tipo_veiculo] || carga.tipo_caminhao || 'Não informado',
        p_carga_titulo: carga.titulo || 'Carga',
        p_carga_origem: carga.origem_cidade || 'N/A',
        p_carga_origem_estado: carga.origem_estado || '',
        p_carga_destino: carga.destino_cidade || 'N/A',
        p_carga_destino_estado: carga.destino_estado || '',
        p_carga_tipo: carga.tipo_carga || 'Não informado',
        p_carga_veiculo: tipoVeiculoLabels[carga.tipo_veiculo] || carga.tipo_caminhao || 'Não informado',
        p_carga_valor: carga.valor_frete || 0,
        p_carga_peso: carga.peso_valor || null,
        p_carga_peso_unidade: carga.peso_unidade || 'ton'
      });

      if (!rpcError && rpcResult?.consulta_id) {
        consultaLocal.id = rpcResult.consulta_id;
        console.log('✅ Consulta salva no Supabase:', rpcResult.consulta_id);
      } else {
        console.warn('⚠️ RPC falhou ou não retornou ID:', rpcError?.message);
      }

    } catch (rpcErr) {
      console.warn('⚠️ Erro RPC (não crítico):', rpcErr.message);
    }

    // ✅ SEMPRE salva no localStorage (independente de erro no Supabase)
    const consultas = JSON.parse(localStorage.getItem('borafrete_consultas') || '[]');
    consultas.push(consultaLocal);
    localStorage.setItem('borafrete_consultas', JSON.stringify(consultas));
    localStorage.setItem('borafrete_consulta_atual', consultaLocal.id);

    toast.success('Solicitação enviada! Aguarde aprovação do admin.');

    // ✅ Envia CARGA COMPLETA no state para PainelEspera
    navigate('/painel-espera', { 
      state: { 
        consultaId: consultaLocal.id,
        carga: carga
      } 
    });
  };

  const handleIrParaContrato = () => {
    if (!carga) {
      toast.error('Carga não encontrada');
      return;
    }
    navigate(`/contrato/${carga.id}`, { state: { carga, from: location.pathname } });
  };

  const formatarData = (data) => {
    if (!data) return 'A combinar';
    try { return format(new Date(data), 'dd/MM/yyyy', { locale: ptBR }); } catch { return data; }
  };

  const getBadge = () => {
    if (carga?.tipo_frete === 'Carga completa' || carga?.tipo_carga === 'Granel') {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 border border-blue-200">Carga completa</span>;
    }
    if (carga?.tipo_frete === 'Carga fracionada' || carga?.tipo_carga === 'Frigorificada') {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700 border border-green-200">Carga fracionada</span>;
    }
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-600 border border-gray-200">{carga?.tipo_carga || 'Carga'}</span>;
  };

  if (isLoadingCarga || authLoading || loadingMotorista) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><Loader2 className="w-8 h-8 text-blue-600 animate-spin" /></div>;
  }

  if (!carga) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="text-center"><h2 className="text-xl font-semibold text-gray-800">Carga não encontrada</h2><Button onClick={() => navigate(-1)} className="mt-4">Voltar</Button></div></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6"><ArrowLeft className="w-4 h-4 mr-2" />Voltar</Button>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-blue-800 to-blue-600 p-6 text-white">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2"><MapPin className="w-5 h-5" /><span className="font-semibold">{carga.origem_cidade} - {carga.origem_estado}</span></div>
                <ArrowRight className="w-5 h-5 text-blue-200" />
                <div className="flex items-center gap-2"><MapPin className="w-5 h-5 text-green-300" /><span className="font-semibold">{carga.destino_cidade} - {carga.destino_estado}</span></div>
              </div>
              <div className="flex items-center gap-2">
                <BotaoOnlinePiscante ativo={carga.status === 'disponivel'} />
                {isPropria && <Badge className="bg-gradient-to-r from-amber-400 to-orange-400 text-white border-0 shadow-lg"><Star className="w-3 h-3 mr-1 fill-white" />SUA CARGA</Badge>}
                <Badge className={statusColors[carga.status] || statusColors.disponivel}>{statusLabels[carga.status] || 'Disponível'}</Badge>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="flex items-center gap-3 mb-6"><h1 className="text-2xl font-bold text-gray-900">{carga.titulo}</h1>{getBadge()}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <InfoBox icon={Package} label="Tipo de carga" value={carga.tipo_carga || 'Não informado'} />
              <InfoBox icon={Weight} label="Peso" value={formatarPeso(carga.peso_valor, carga.peso_unidade)} />
              <InfoBox icon={Truck} label="Carroceria" value={tipoCarroceriaLabels[carga.tipo_carroceria] || carga.tipo_carroceria || 'Não informada'} />
              <InfoBox icon={Truck} label="Veículo" value={tipoVeiculoLabels[carga.tipo_veiculo] || carga.tipo_caminhao || 'Não informado'} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <InfoBox icon={Calendar} label="Data de Coleta" value={`${formatarData(carga.data_coleta || carga.data_retirada)} às ${carga.hora_coleta || '08:00'}`} />
              <InfoBox icon={Calendar} label="Data de Entrega" value={`${formatarData(carga.data_entrega)} às ${carga.hora_entrega || '18:00'}`} />
            </div>
            
            {/* ✅ CORREÇÃO: Mostra APENAS valor_frete (não valor_total multiplicado) */}
            <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-green-800">Valor do Frete</span>
              </div>
              <p className="text-3xl font-bold text-green-700">
                {formatarValor(carga.valor_frete)}
              </p>
              {carga.tipo_valor && (
                <Badge className="mt-2 bg-green-100 text-green-700">
                  {carga.tipo_valor === 'fixo' ? 'Valor Fixo' : 
                   carga.tipo_valor === 'tonelada' ? 'Por Tonelada' : 
                   carga.tipo_valor === 'metro_cubico' ? 'Por m³' : 'A Combinar'}
                </Badge>
              )}
              {carga.peso_valor && carga.peso_unidade && (
                <p className="text-sm text-green-600 mt-2">
                  Peso: {formatarPeso(carga.peso_valor, carga.peso_unidade)}
                </p>
              )}
            </div>
            
            {carga.descricao && <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-100"><h3 className="font-semibold text-gray-800 mb-2">Descrição da Carga</h3><p className="text-gray-600 whitespace-pre-wrap text-sm leading-relaxed">{carga.descricao}</p></div>}
            {!isPropria && (
              <div className="space-y-3">
                {jaTemContrato ? (
                  <>
                    <div className="flex items-center gap-2 p-4 bg-green-100 rounded-lg border border-green-200 mb-3"><CheckCircle className="w-5 h-5 text-green-600" /><span className="text-sm font-medium text-green-800">Pagamento confirmado - Contrato liberado</span></div>
                    <Button onClick={handleIrParaContrato} className="w-full h-12 rounded-xl font-semibold bg-blue-600 hover:bg-blue-700 gap-2"><FileText className="w-5 h-5" />Ver Contrato</Button>
                  </>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    <Button 
                      onClick={handleContratarCarga} 
                      className="w-full h-14 rounded-xl font-bold text-base bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 gap-2 shadow-lg shadow-green-200 transition-all transform hover:scale-[1.02]"
                    >
                      <Bell className="w-5 h-5 animate-pulse" />
                      <div className="text-left">
                        <div>CONTRATAR CARGA</div>
                        <div className="text-xs text-green-200 font-normal">Aguarde aprovação do admin</div>
                      </div>
                    </Button>
                  </div>
                )}
              </div>
            )}
            {isPropria && (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-5 h-5 text-amber-600" /><span className="font-medium text-amber-800">Sua Carga</span></div>
                <p className="text-sm text-amber-700 mb-3">Você está visualizando sua própria carga cadastrada. Gerencie-a no painel de cargas.</p>
                <Button onClick={() => navigate('/minhas-cargas')} variant="outline" className="w-full border-amber-500 text-amber-700 hover:bg-amber-100">Ir para Minhas Cargas</Button>
              </div>
            )}
            {jaTemContrato && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-3"><Building2 className="w-5 h-5 text-blue-600" /><h3 className="font-semibold text-blue-800">Empresa Contratante</h3></div>
                <div className="grid grid-cols-1 gap-4">
                  <div><p className="text-sm text-blue-600">Empresa</p><p className="font-medium text-blue-900">{carga.empresa_nome || 'Confidencial'}</p></div>
                  <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-blue-100"><Shield className="w-4 h-4 text-green-600" /><p className="text-xs text-gray-600">Contato da empresa será liberado após confirmação do pagamento.</p></div>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* ❌ WhatsAppButton REMOVIDO — não sai mais da tela */}
      </div>
    </div>
  );
}